import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { z } from 'zod';
import logo from '@/assets/bunadstuggu-logo.png';
import heroBg from '@/assets/hero-bunadstuggu.jpg';

const loginSchema = z.object({
  username: z.string().trim().min(1, 'Brukernavn er påkrevd').max(100, 'Brukernavn kan ikke være lenger enn 100 tegn'),
  password: z.string().min(8, 'Passord må være minst 8 tegn')
    .max(128, 'Passord kan ikke være lenger enn 128 tegn')
    .regex(/[A-Z]/, 'Passord må inneholde minst én stor bokstav')
    .regex(/[0-9]/, 'Passord må inneholde minst ett tall'),
});

const Auth = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});
  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = loginSchema.safeParse({ username, password });
    if (!result.success) {
      const fieldErrors: { username?: string; password?: string } = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as 'username' | 'password';
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);

    try {
      const email = `${result.data.username.toLowerCase()}@bunadstuggu.no`;
      const { error } = await signIn(email, result.data.password);
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Feil brukernavn eller passord');
        } else {
          toast.error('Innlogging feilet. Prøv igjen.');
        }
      } else {
        toast.success('Logget inn!');
        navigate('/admin');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${heroBg})` }}
    >
      <div className="absolute inset-0 bg-charcoal/40" />
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-8">
            <img src={logo} alt="Bunadstuggu" className="h-16" />
          </div>
          
          <h1 className="text-2xl font-serif text-charcoal text-center mb-6">
            Logg inn
          </h1>
          
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="username">Brukernavn</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setErrors(prev => ({ ...prev, username: undefined })); }}
                required
                className="mt-1"
                placeholder="Brukernavn"
                maxLength={100}
              />
              {errors.username && <p className="text-sm text-destructive mt-1">{errors.username}</p>}
            </div>
            
            <div>
              <Label htmlFor="password">Passord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: undefined })); }}
                required
                className="mt-1"
                maxLength={128}
              />
              {errors.password && <p className="text-sm text-destructive mt-1">{errors.password}</p>}
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Vennligst vent...' : 'Logg inn'}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <a href="/" className="text-charcoal/60 hover:text-charcoal text-sm">
              ← Tilbake til forsiden
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
